import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment/moment';
import { useWindowWidth } from '@react-hook/window-size';
import { FaTable } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import DatePicker from '../../components/DatePicker';
import SelectBox from '../../components/SelectBox';
import CustomSearchBtn from '../../components/CustomSearchBtn';
import CustomNoData from '../../components/CustomNoData';
import { LoadingSpinner } from '../../components';
import OddsCard from './components/OddsCard';
import OddsTableView from './components/OddsTableView';
import { oddsHubMarkets } from '../../utils/oddsHubDemoData';
import differentOddService from '../../services/differentOddService';
import { setToastMessage } from '../../reducers/common.reducer';
import { connectSocket } from '../../utils/socket';

const HIGHLIGHT_DURATION_MS = 5000;
const BLINK_ANIMATION_NAME = 'oddsHubBlink';
const SOCKET_STATUS = {
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
};
const SORT_OPTIONS = [
  { id: 'gap', name: 'Gap' },
  { id: 'status', name: 'Status Live to FT' },
  { id: 'lastChanges', name: 'Last Changes' },
];
const STATUS_SORT_ORDER = {
  Live: 0,
  FT: 1,
};

const toSortableTimestamp = (value) => {
  if (!value) {
    return 0;
  }

  const parsedTime = new Date(value).getTime();
  return Number.isFinite(parsedTime) ? parsedTime : 0;
};

const toSortableNumber = (value) => {
  if (value === undefined || value === null || value === '') {
    return 0;
  }

  const parsedValue = Number.parseFloat(String(value).replace(/,/g, ''));
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getSortableGap = (match) => {
  const gapValue =
    match?.gap ?? match?.oddGap ?? match?.oddsGap ?? match?.gapValue ?? match?.difference ?? match?.diff;

  if (gapValue !== undefined && gapValue !== null && gapValue !== '') {
    return Math.abs(toSortableNumber(gapValue));
  }

  return match?.oddFlag === 'different' ? 1 : 0;
};

const compareByLastChanges = (leftMatch, rightMatch) => {
  return toSortableTimestamp(rightMatch?.createdAt) - toSortableTimestamp(leftMatch?.createdAt);
};

const normalizeOddsHubData = (payload) => {
  const rawList = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.payload)
      ? payload.payload
      : Array.isArray(payload)
        ? payload
        : [];

  return rawList.map((match, index) => ({
    ...match,
    createdAt: match.createdAt,
    homeUpper: match.homeUpper,
    status: moment(match.kickoff).isAfter(moment()) ? "Live" : "FT",
    id: match.id ?? match.event_id ?? match.eventId ?? `odds-hub-${index}`,
    eventId: String(match.eventId ?? match.event_id ?? match.id ?? `odds-hub-${index}`),
    league: match.league ?? match.league_name ?? match.competition ?? 'Unknown League',
    kickoff: match.kickoff ?? match.kickoff_time ?? match.event_time ?? match.matchTime ?? '',
    base: (Array.isArray(match.base) ? match.base : Array.isArray(match.baseOdds) ? match.baseOdds : []).filter(Boolean),
    reference: (Array.isArray(match.reference) ? match.reference : Array.isArray(match.referenceOdds) ? match.referenceOdds : []).filter(Boolean),
  }));
};

const normalizeCronUpdates = (payload) => {
  const rawList = Array.isArray(payload?.message)
    ? payload.message
    : Array.isArray(payload?.payload)
      ? payload.payload
      : Array.isArray(payload)
        ? payload
        : payload?.message && typeof payload.message === 'object'
          ? [payload.message]
          : [];

  return rawList
    .map((item) => {
      const eventId = item?.event_id ?? item?.eventId ?? item?.id ?? item?.base?.[0]?.id ?? item?.reference?.[0]?.id;

      if (eventId === undefined || eventId === null || eventId === '') {
        return null;
      }

      return {
        status:
          item?.status ?? (item?.kickoff ? (moment(item.kickoff).isAfter(moment()) ? "Live" : "FT") : undefined),
        createdAt: item?.createdAt,
        gap: item?.gap,
        oddGap: item?.oddGap,
        oddsGap: item?.oddsGap,
        gapValue: item?.gapValue,
        difference: item?.difference,
        diff: item?.diff,
        oddFlag: item?.oddFlag ?? item?.odd_flag,
        eventId: String(eventId),
        base: Array.isArray(item?.base) ? item.base.filter(Boolean) : [],
        reference: Array.isArray(item?.reference) ? item.reference.filter(Boolean) : [],
      };
    })
    .filter(Boolean);
};

const OddsHub = () => {
  const dispatch = useDispatch();
  const onlyWidth = useWindowWidth();
  const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMarket, setSelectedMarket] = useState(oddsHubMarkets[0]);
  const [selectedSort, setSelectedSort] = useState(SORT_OPTIONS[2]);
  const [viewMode, setViewMode] = useState('table');
  const [oddsData, setOddData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedEvents, setHighlightedEvents] = useState({});
  const [socketStatus, setSocketStatus] = useState(SOCKET_STATUS.DISCONNECTED);
  const oddsDataRef = useRef([]);
  const highlightTimeoutsRef = useRef(new Map());

  const leagues = useMemo(() => {
    const uniqueLeagues = new Map();
    oddsData.forEach((match) => {
      const leagueId = match.league ?? 'unknown';
      const leagueName = match.league ?? 'Unknown League';
      if (!uniqueLeagues.has(leagueId)) {
        uniqueLeagues.set(leagueId, { id: leagueId, name: leagueName });
      }
    });
    return [{ id: 'all', name: 'All Leagues' }, ...uniqueLeagues.values()];
  }, [oddsData]);

  const [selectedLeague, setSelectedLeague] = useState(leagues[0]);

  useEffect(() => {
    if (leagues.length > 0) {
      setSelectedLeague((currentLeague) => {
        if (!currentLeague) {
          return leagues[0];
        }

        return leagues.find((league) => league.id === currentLeague.id) ?? leagues[0];
      });
    }
  }, [leagues]);

  const counting = useMemo(() => {
    const allTotal = oddsData.length;
    const referencesWithData = oddsData.filter((match) => (match.reference?.length ?? 0) > 0).length;
    return {
      allTotal,
      matchingTotal: referencesWithData,
    };
  }, [oddsData]);

  const filteredMatches = useMemo(() => {
    const leagueId = selectedLeague?.id ?? 'all';
    const keyword = searchTerm.trim().toLowerCase();
    const marketId = selectedMarket?.id ?? 'all';

    return oddsData
      .filter((match) => {
        if (leagueId === 'all') {
          return true;
        }
        const matchLeague = match.league ?? '';
        return matchLeague === leagueId;
      })
      .map((match) => ({
        ...match,
        base: Array.isArray(match.base) ? match.base : [],
        reference: Array.isArray(match.reference) ? match.reference : [],
      }))
      .filter((match) => {
        if (!keyword) {
          return true;
        }
        const pools = [...match.base, ...match.reference];
        return pools.some((bookmaker) => bookmaker.matchup?.toLowerCase().includes(keyword));
      })
      .filter((match) => {
        if (marketId === 'all') {
          return true;
        }
        const pools = [...match.base, ...match.reference];
        return pools.some((bookmaker) => Boolean(bookmaker?.[marketId]));
      });
  }, [oddsData, selectedLeague, searchTerm, selectedMarket]);

  const sortedFilteredMatches = useMemo(() => {
    return [...filteredMatches].sort((leftMatch, rightMatch) => {
      if (selectedSort?.id === 'gap') {
        return getSortableGap(rightMatch) - getSortableGap(leftMatch) || compareByLastChanges(leftMatch, rightMatch);
      }

      if (selectedSort?.id === 'status') {
        const leftStatusOrder = STATUS_SORT_ORDER[leftMatch?.status] ?? Number.MAX_SAFE_INTEGER;
        const rightStatusOrder = STATUS_SORT_ORDER[rightMatch?.status] ?? Number.MAX_SAFE_INTEGER;

        return leftStatusOrder - rightStatusOrder || compareByLastChanges(leftMatch, rightMatch);
      }

      return compareByLastChanges(leftMatch, rightMatch);
    });
  }, [filteredMatches, selectedSort]);

  const viewOptions = [
   // { id: 'card', label: 'Card View', icon: FaRegSquare },
    { id: 'table', label: 'Table View', icon: FaTable },
  ];

  const fetchOddsHubData = async () => {
    setIsLoading(true);

    try {
      const formattedDate = selectedDate || moment().format('YYYY-MM-DD');
      const response = await differentOddService.getOddHubData(formattedDate);
      const normalizedData = normalizeOddsHubData(response?.data);

      oddsDataRef.current = normalizedData;
      setOddData(normalizedData);
    } catch (error) {
      oddsDataRef.current = [];
      setOddData([]);
      dispatch(
        setToastMessage({
          type: 'error',
          message: error?.response?.data?.message || error?.message || 'Unable to load odds hub data.',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    fetchOddsHubData();
  };

  useEffect(() => {
    oddsDataRef.current = oddsData;
  }, [oddsData]);

  useEffect(() => {
    fetchOddsHubData();
  }, []);

  useEffect(() => {
    const socket = connectSocket();

    const handleConnect = () => {
      setSocketStatus(SOCKET_STATUS.CONNECTED);
    };

    const handleDisconnect = () => {
      setSocketStatus(SOCKET_STATUS.DISCONNECTED);
    };

    setSocketStatus(socket.connected ? SOCKET_STATUS.CONNECTED : SOCKET_STATUS.DISCONNECTED);

    const scheduleHighlight = (eventId) => {
      const normalizedEventId = String(eventId);
      const existingTimeout = highlightTimeoutsRef.current.get(normalizedEventId);

      if (existingTimeout) {
        window.clearTimeout(existingTimeout);
      }

      setHighlightedEvents((current) => ({
        ...current,
        [normalizedEventId]: true,
      }));

      const timeoutId = window.setTimeout(() => {
        setHighlightedEvents((current) => {
          const next = { ...current };
          delete next[normalizedEventId];
          return next;
        });
        highlightTimeoutsRef.current.delete(normalizedEventId);
      }, HIGHLIGHT_DURATION_MS);

      highlightTimeoutsRef.current.set(normalizedEventId, timeoutId);
    };

    const onCronUpdate = (payload) => {
      const updates = normalizeCronUpdates(payload);

      if (!updates.length) {
        return;
      }

      const updatesByEventId = new Map(updates.map((item) => [item.eventId, item]));
      const currentData = oddsDataRef.current;
      const matchedEventIds = [];
      const nextData = currentData.map((match) => {
        const matchEventId = String(match.eventId ?? match.id ?? '');
        const incomingUpdate = updatesByEventId.get(matchEventId);

        if (!incomingUpdate) {
          return match;
        }

        matchedEventIds.push(matchEventId);

        return {
          ...match,
          createdAt: incomingUpdate.createdAt ?? new Date().toISOString(),
          status: incomingUpdate.status ?? match.status,
          gap: incomingUpdate.gap ?? match.gap,
          oddGap: incomingUpdate.oddGap ?? match.oddGap,
          oddsGap: incomingUpdate.oddsGap ?? match.oddsGap,
          gapValue: incomingUpdate.gapValue ?? match.gapValue,
          difference: incomingUpdate.difference ?? match.difference,
          diff: incomingUpdate.diff ?? match.diff,
          oddFlag: incomingUpdate.oddFlag ?? match.oddFlag,
          base: incomingUpdate.base,
          reference: incomingUpdate.reference,
        };
      });

      if (!matchedEventIds.length) {
        return;
      }

      oddsDataRef.current = nextData;
      setOddData(nextData);

      matchedEventIds.forEach(scheduleHighlight);
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleDisconnect);
    socket.on('cron:update', onCronUpdate);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleDisconnect);
      socket.off('cron:update', onCronUpdate);
    };
  }, []);

  useEffect(() => {
    return () => {
      highlightTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      highlightTimeoutsRef.current.clear();
    };
  }, []);

  const isSocketOnline = socketStatus === SOCKET_STATUS.CONNECTED;

  return (
    <div className="min-h-screen w-full bg-customGray dark:bg-custom_dark_background px-3 py-2">
      <style>
        {`
          @keyframes ${BLINK_ANIMATION_NAME} {
            0%, 100% { box-shadow: 0 0 0 rgba(249, 115, 22, 0); }
            50% { box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.35); }
          }
        `}
      </style>
      <div className="flex justify-between items-center">
        <span className="text-base text-background dark:text-customWhite font-semibold">Odds Hub</span>
        <div className="flex text-xs divide-x divide-customBlack/80 dark:divide-custom_dark_secondary text-background dark:text-customLightMilkWhite">
          <span className="px-1">Total - {counting.allTotal}</span>
          <span className="px-1">Matching - {counting.matchingTotal}</span>
        </div>
      </div>

      <div className="mx-auto flex flex-col gap-2">
        <div className="rounded-lg border border-transparent bg-white p-2 shadow-lg dark:border-custom_dark_secondary dark:bg-custom_dark_primary2">
          <div className="grid grid-cols-1 gap-2 md:gap-3 md:grid-cols-8 items-center">
            <div className="md:col-span-1">
              <DatePicker date={selectedDate} setDate={setSelectedDate} isMobile={onlyWidth < 768} />
            </div>
            <div className="md:col-span-1">
              <CustomSearchBtn handleSearch={handleSearch} extraStyle="mt-2" />
            </div>
            <div className="md:col-span-2">
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-customMuted dark:text-customMuted">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                    <path
                      fillRule="evenodd"
                      d="M10.5 3.75a6.75 6.75 0 1 0 4.245 12.012l4.246 4.246a.75.75 0 1 0 1.06-1.06l-4.246-4.247A6.75 6.75 0 0 0 10.5 3.75Zm-5.25 6.75a5.25 5.25 0 1 1 10.5 0 5.25 5.25 0 0 1-10.5 0Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search teams..."
                  className={`${
                    onlyWidth < 768 ? 'text-xs' : 'text-sm'
                  } w-full rounded-lg border border-customLightMilkWhite bg-customBlack/5 py-2 pl-11 pr-4 font-medium text-customBlack shadow-sm focus:outline-none focus:ring-2 focus:ring-customLightMilkWhite dark:border-custom_dark_secondary dark:bg-custom_dark_primary dark:text-customLightMilkWhite dark:placeholder:text-customMuted dark:focus:ring-customAccent/40`}
                />
              </div>
            </div>
            <div className="md:col-span-1">
              <SelectBox selected={selectedLeague} setSelected={setSelectedLeague} options={leagues} isMobile={onlyWidth < 768} />
            </div>
            <div className="md:col-span-1">
              <div className="flex flex-col gap-1">
                {/* <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-customSecondaryText dark:text-customFontMilkWhite/60">
                  Sort by:
                </span> */}
                <SelectBox selected={selectedSort} setSelected={setSelectedSort} options={SORT_OPTIONS} isMobile={onlyWidth < 768} />
              </div>
            </div>
            {/* <div className="md:col-span-1">
              <SelectBox selected={selectedMarket} setSelected={setSelectedMarket} options={oddsHubMarkets} isMobile={onlyWidth < 768} />
            </div> */}
            {/* <div className="md:col-span-1">
              <div className="flex gap-2">
                <div className="rounded-2xl border border-customSlate bg-customGray/80 p-1 shadow-sm dark:border-custom_dark_secondary dark:bg-custom_dark_primary/60">
                  <div className="grid grid-cols-1 gap-1">
                    {viewOptions.map(({ id, label, icon: Icon }) => {
                      const isActive = viewMode === id;

                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setViewMode(id)}
                          className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition-all ${
                            isActive
                              ? 'bg-customAccent text-white shadow-sm'
                              : 'text-customSecondaryText hover:bg-white hover:text-customAccent dark:text-customFontMilkWhite/75 dark:hover:bg-custom_dark_primary2 dark:hover:text-customFontMilkWhite'
                          }`}
                        >
                          <Icon className="text-[11px]" />
                          <span>{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div> */}
            <div className="md:col-span-2">
              <div
                  className={`rounded-2xl border px-3 py-2 shadow-sm transition-all ${
                    isSocketOnline
                      ? 'border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-emerald-100/80'
                      : 'border-red-200 bg-gradient-to-r from-red-50 via-white to-red-100/80'
                  } ${
                    isSocketOnline
                      ? 'dark:border-emerald-500/30 dark:bg-gradient-to-r dark:from-custom_dark_primary2 dark:via-custom_dark_primary dark:to-emerald-500/10'
                      : 'dark:border-rose-500/30 dark:bg-gradient-to-r dark:from-custom_dark_primary2 dark:via-custom_dark_primary dark:to-rose-500/10'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-customSecondaryText dark:text-customMuted">
                        Socket Status
                      </p>
                      <p className={`text-sm font-bold ${isSocketOnline ? 'text-emerald-600 dark:text-emerald-300' : 'text-red-600 dark:text-rose-300'}`}>
                        {isSocketOnline ? 'Online' : 'Offline'}
                      </p>
                    </div>
                    <div
                      className={`flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold ${
                        isSocketOnline
                          ? 'bg-emerald-100 text-emerald-700 dark:border dark:border-emerald-500/20 dark:bg-emerald-500/20 dark:text-emerald-200'
                          : 'bg-red-100 text-red-700 dark:border dark:border-rose-500/20 dark:bg-rose-500/12 dark:text-rose-200'
                      }`}
                    >
                      <span
                        className={`h-2.5 w-2.5 rounded-full ${
                          isSocketOnline
                            ? 'bg-emerald-500 shadow-[0_0_0_4px_rgba(34,197,94,0.18)]'
                            : 'bg-red-500 shadow-[0_0_0_4px_rgba(239,68,68,0.18)]'
                        }`}
                      />
                      <span>{isSocketOnline ? 'Live feed connected' : 'Feed disconnected'}</span>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            {viewMode === 'card' ? (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-5">
                {sortedFilteredMatches.map((match, index) => (
                  <OddsCard
                    key={match.eventId ?? `${match.league}-${index}`}
                    league={match.league}
                    kickoff={match.kickoff}
                    base={match.base}
                    reference={match.reference}
                    selectedMarket={selectedMarket?.id}
                    isHighlighted={Boolean(highlightedEvents[match.eventId])}
                    highlightAnimationName={BLINK_ANIMATION_NAME}
                  />
                ))}
              </div>
            ) : (
              <OddsTableView
                matches={sortedFilteredMatches}
                selectedDate={selectedDate}
                highlightedEvents={highlightedEvents}
                highlightAnimationName={BLINK_ANIMATION_NAME}
              />
            )}
            {filteredMatches.length === 0 && <CustomNoData />}
          </>
        )}
      </div>
    </div>
  );
};

export default OddsHub;
